import './AgentReply.css'
import { useEffect, useState } from 'react'
import type { AgentWidget } from 'aevocam-rest-api-spec'
import { MarkdownViewer } from '@/components/scalar/MarkdownViewer'
import { AgentWidgetView } from './AgentWidgetView'
import { widgetHasVisibleUi } from './widgetVisibility'
import { cn } from '@/utilities/misc'
import { Button } from '../action/Button'

type Props = {
  content: string
  widgets?: AgentWidget[]
  reasoning?: string
  /** True while this reply's reasoning is still streaming in */
  reasoningStreaming?: boolean
  /** True when widgets just arrived from the live stream for this turn */
  fromStream?: boolean
  onStreamConsumed?: () => void
  setArticleIds?: (articleIds?: string[]) => void
  onRefreshEditor?: () => void
}

export function AgentReply({
  content,
  widgets,
  reasoning,
  reasoningStreaming,
  fromStream,
  onStreamConsumed,
  setArticleIds,
  onRefreshEditor,
}: Props) {

  const [reasoningExpanded, setReasoningExpanded] = useState(!!reasoningStreaming)

  useEffect(() => {
    if (!fromStream) return
    onStreamConsumed?.()
  }, [fromStream]) // onStreamConsumed intentionally omitted — one-shot clear

  useEffect(() => {
    setReasoningExpanded(!!reasoningStreaming)
  }, [reasoningStreaming])

  const visibleWidgets = widgets?.filter(widgetHasVisibleUi) ?? []
  const invisibleWidgets = widgets?.filter(widget => !widgetHasVisibleUi(widget)) ?? []
  const hasReasoning = !!reasoning && reasoning.length > 0

  return (
    <div className='AgentReply'>
      {hasReasoning && (
        <div className={cn('Reasoning', reasoningExpanded && 'Expanded')}>
          <Button
            variant='raw'
            label={reasoningExpanded ? 'reasoning' : 'reasoning'}
            icon={reasoningExpanded ? 'chevronUp' : 'chevronDown'}
            className='Toggle'
            onClick={() => setReasoningExpanded(prev => !prev)}
          />
          {reasoningExpanded && (
            <div className='Body'>
              <MarkdownViewer value={reasoning} />
            </div>
          )}
        </div>
      )}
      <MarkdownViewer value={content} />
      {invisibleWidgets.map((widget, index) => (
        <AgentWidgetView
          key={`action-${widget.placeholder}-${index}`}
          widget={widget}
          fromStream={fromStream}
          onRefreshEditor={onRefreshEditor}
        />
      ))}
      {visibleWidgets.length > 0 && (
        <div className='Widgets'>
          {visibleWidgets.map((widget, index) => (
            <div
              key={`${widget.placeholder}-${index}`}
              className='Widget'
            >
              <AgentWidgetView
                widget={widget}
                fromStream={fromStream}
                setArticleIds={setArticleIds}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
